import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { useWhoAmI } from '../../auth/AdminGate';
import {
  CategorySalesPeriod,
  DailySalesReport,
  DailySalesRow,
  getDailySalesReport,
} from '../../api';
import RoughLine, { LineSeries } from '../widgets/charts/RoughLine';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; report: DailySalesReport };

// Same window keys as the category report; this one opens on 3 months
// since a weather trend needs more than a few weeks to read.
const PERIODS: { key: CategorySalesPeriod; label: string }[] = [
  { key: 'month', label: 'Month' },
  { key: '3months', label: '3 Months' },
  { key: '6months', label: '6 Months' },
  { key: 'year', label: 'Year' },
];

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// "YYYY-MM-DD" → "MMM D", parsed by hand so no timezone shifts the day.
function shortDay(iso: string): string {
  const [, m, d] = iso.split('-');
  const mi = Number(m) - 1;
  return `${MONTH_LABELS[mi] ?? m} ${Number(d)}`;
}

const CAD = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
});

// Derive an at-a-glance condition from the day's *average* character
// rather than Open-Meteo's daily `weather_code` — that code is the day's
// worst moment (one overcast hour → "overcast"; a 0.9 mm sprinkle →
// "drizzle"), which made sunny days read as cloudy/rainy. Instead: decide
// wet vs dry from precip amount, then (when dry) sunny vs cloudy from mean
// cloud cover.
//
// Thresholds are deliberately forgiving on the low end so trace precip
// (Open-Meteo/ERA5 has a light-drizzle bias) doesn't paint a dry day wet.
function condition(row: DailySalesRow): { icon: string; label: string } {
  const precip = row.precip_mm ?? 0;
  const snow = row.snowfall_cm ?? 0;
  const cloud = row.cloud_cover_mean;

  // Snow only when it's the majority of the day's precipitation (snow in
  // cm ≈ its liquid-equivalent mm here, close enough for a share test) —
  // otherwise a cm of slush on an otherwise rainy day mislabels the day.
  if (snow >= 1 && snow >= 0.5 * precip) {
    return precip >= 10
      ? { icon: '❄️', label: 'Snow' }
      : { icon: '🌨️', label: 'Snow' };
  }
  if (precip >= 10) return { icon: '🌧️', label: 'Rain' };
  if (precip >= 2.5) return { icon: '🌧️', label: 'Rain' };
  if (precip >= 1) return { icon: '🌦️', label: 'Showers' };

  // Dry day — classify the sky by mean cloud cover.
  if (cloud === undefined) return { icon: '—', label: '' };
  if (cloud < 25) return { icon: '☀️', label: 'Sunny' };
  if (cloud < 55) return { icon: '🌤️', label: 'Mostly sunny' };
  if (cloud < 80) return { icon: '⛅', label: 'Partly cloudy' };
  return { icon: '☁️', label: 'Cloudy' };
}

const DailySalesReportPage = () => {
  const { idToken } = useAuth();
  const isGod = useWhoAmI().roles.includes('god');
  const [period, setPeriod] = useState<CategorySalesPeriod>('3months');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    // God-only report — don't even fire the request for a non-god (the
    // endpoint would 403 anyway).
    if (!idToken || !isGod) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    getDailySalesReport(idToken, period)
      .then((report) => {
        if (!cancelled) setState({ kind: 'ready', report });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message:
              (err as Error)?.message || 'failed to load daily sales report',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, period, isGod]);

  if (!isGod) {
    return (
      <Alert severity="warning">
        This report is restricted to god-role users.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
        Daily sales &amp; weather
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        Bridgewater in-store sales per day against that day&apos;s weather.
        Sales are post-tax dollars from reconciled Square transactions;
        weather is from Open-Meteo (Bridgewater, NS) and lags ~5 days, so the
        most recent days show sales without weather.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={period}
        onChange={(_, v: CategorySalesPeriod | null) => v && setPeriod(v)}
        aria-label="period"
        sx={{ mb: 3 }}
      >
        {PERIODS.map((p) => (
          <ToggleButton key={p.key} value={p.key}>
            {p.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {state.kind === 'error' && <Alert severity="error">{state.message}</Alert>}

      {state.kind === 'ready' && <ReadyView report={state.report} />}
    </Box>
  );
};

const ReadyView = ({ report }: { report: DailySalesReport }) => {
  const { days } = report;

  // X-axis labels + the sales line (full length). Charts scroll
  // horizontally, so a wide window stays legible.
  const xLabels = useMemo(() => days.map((r) => shortDay(r.day)), [days]);
  const salesSeries: LineSeries[] = useMemo(
    () => [{ name: 'Sales ($)', values: days.map((r) => r.total) }],
    [days],
  );

  // Temperature lines. Missing weather is only ever the recent tail
  // (archive lag), and RoughLine coerces undefined → 0 (which would draw
  // a false drop to 0 °C). So truncate both temp series at the last day
  // that actually has a reading; rough-viz just ends the line there.
  const tempSeries: LineSeries[] = useMemo(() => {
    let last = -1;
    days.forEach((r, i) => {
      if (r.temp_max_c !== undefined) last = i;
    });
    if (last < 0) return [];
    const highs = days.slice(0, last + 1).map((r) => r.temp_max_c ?? 0);
    const lows = days.slice(0, last + 1).map((r) => r.temp_min_c ?? 0);
    return [
      { name: 'High °C', values: highs },
      { name: 'Low °C', values: lows },
    ];
  }, [days]);

  const chartWidth = Math.max(1100, days.length * 12);

  if (days.length === 0) {
    return <Alert severity="info">No days in this window.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
          Sales
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <RoughLine
            xLabels={xLabels}
            series={salesSeries}
            yLabel="Sales ($)"
            colors={['#3b6fb0']}
            roughness={1}
            fontStyle={1}
            width={chartWidth}
            height={340}
            bulletRadius={2}
            yValueFormat="$,.0f"
            axisFontSize="0.85rem"
            tooltipFontSize="1.1rem"
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
          Temperature
        </Typography>
        {tempSeries.length === 0 ? (
          <Alert severity="info">
            No weather cached for this window yet. Reload in a moment — the
            first load backfills it.
          </Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <RoughLine
              xLabels={xLabels}
              series={tempSeries}
              yLabel="°C"
              colors={['#c2410c', '#2563eb']}
              roughness={1}
              fontStyle={1}
              width={chartWidth}
              height={300}
              bulletRadius={2}
              yValueFormat=".0f"
              axisFontSize="0.85rem"
              tooltipFontSize="1.1rem"
            />
          </Box>
        )}
      </Paper>

      <DayTable days={days} />
    </Stack>
  );
};

const DayTable = ({ days }: { days: DailySalesRow[] }) => {
  // Most-recent day first — the operator usually cares about the tail.
  const rows = useMemo(() => [...days].reverse(), [days]);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
        By day
      </Typography>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Sales</TableCell>
              <TableCell align="right">Txns</TableCell>
              <TableCell align="right">High</TableCell>
              <TableCell align="right">Low</TableCell>
              <TableCell align="right">Precip</TableCell>
              <TableCell>Weather</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const w = condition(r);
              const precip =
                (r.precip_mm ?? 0) > 0
                  ? `${r.precip_mm!.toFixed(1)} mm`
                  : (r.snowfall_cm ?? 0) > 0
                    ? `${r.snowfall_cm!.toFixed(1)} cm ❄`
                    : '—';
              return (
                <TableRow key={r.day} hover>
                  <TableCell>{shortDay(r.day)}</TableCell>
                  <TableCell align="right">{CAD.format(r.total)}</TableCell>
                  <TableCell align="right">{r.transaction_count}</TableCell>
                  <TableCell align="right">
                    {r.temp_max_c !== undefined ? `${Math.round(r.temp_max_c)}°` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {r.temp_min_c !== undefined ? `${Math.round(r.temp_min_c)}°` : '—'}
                  </TableCell>
                  <TableCell align="right">{precip}</TableCell>
                  <TableCell>
                    <span style={{ marginRight: 6 }}>{w.icon}</span>
                    <span style={{ color: '#64748b' }}>{w.label}</span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DailySalesReportPage;
